/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import { createPortal } from "react-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ExploreIcon from "@mui/icons-material/Explore";
import HistoryIcon from "@mui/icons-material/History";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import { format } from "date-fns";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import knowledgeArticleService from "../../../service/knowledgeArticle.service";
import { encryptStorageUtils } from "../../../service/encryptStorage";
import { showSnackbar } from "../../../service/snackbar.service";
import { IAssistanceLog, IKnowledgeMatch } from "../../../types/knowledgeArticle";
import NavLinks from "../Navbar/NavLinks";
import ContactSupportDialog from "../ContactSupportDialog";
import AssistanceMarkdown from "./AssistanceMarkdown";
import AskAnythingButton from "./AskAnythingButton";
import {
  ASSISTANCE_DRAWER_WIDTH_PX,
  useUserAssistance,
} from "./UserAssistanceContext";
import { useAppContext } from "../../../contexts/useStorageContext";
import { UserRoles } from "../../../types/auth";

type ChatReply = {
  id: string;
  text: string;
  matches?: IKnowledgeMatch[];
  fromAdmin?: boolean;
};

type ChatTurn = {
  id: string;
  question: string;
  createdAt?: Date | string;
  replies: ChatReply[];
  unreadAdminUpdate?: boolean;
};

type ChatThread = ChatTurn & {
  followups: ChatTurn[];
};

const logToTurn = (log: IAssistanceLog): ChatTurn => ({
  id: log._id,
  question: log.question,
  createdAt: log.createdAt,
  unreadAdminUpdate: !!log.adminNote && log.adminNoteSeen !== true,
  replies: [
    ...(log.answer ? [{ id: `${log._id}-a`, text: log.answer }] : []),
    ...(log.adminNote
      ? [{ id: `${log._id}-admin`, text: log.adminNote, fromAdmin: true }]
      : []),
  ],
});

const logsToThreads = (logs: IAssistanceLog[]): ChatThread[] => {
  const ids = new Set(logs.map((log) => log._id));
  const followupsByParent = new Map<string, ChatTurn[]>();
  const roots: ChatThread[] = [];
  logs.forEach((log) => {
    const parentId = log.parentLogId && ids.has(log.parentLogId) ? log.parentLogId : "";
    if (!parentId) {
      roots.push({ ...logToTurn(log), followups: [] });
      return;
    }
    const followups = followupsByParent.get(parentId) || [];
    followups.push(logToTurn(log));
    followupsByParent.set(parentId, followups);
  });
  return roots.map((thread) => ({
    ...thread,
    followups: followupsByParent.get(thread.id) || [],
  }));
};

const threadHasUnread = (thread: ChatThread) =>
  !!thread.unreadAdminUpdate || thread.followups.some((followup) => followup.unreadAdminUpdate);

const SUGGESTED_QUESTIONS = [
  "Show me samples uploaded this week",
  "Show handheld samples",
  "Clear the filters",
  "Find sample 123",
  "Filter the Dashboard by TMR",
  "Open the gas report for sample 123",
  "What can I do on the Dashboard?",
  "How do I upload samples?",
  "Where is Farm Management?",
  "What does crop type mean?",
  "Who can see which menus?",
];

const assistancePanelBg = (theme: { palette: { mode: string } }) =>
  theme.palette.mode === "dark" ? "#152033" : "#f0f0f0";

const pathLabel = (path?: string) => {
  if (!path) return "";
  const nav = NavLinks.find((item) => item.path === path);
  return nav?.menu_title || path;
};

export default function UserAssistancePanel({ width }: { width: string }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const { open, setOpen, setUnreadCount, requestAction } = useUserAssistance();
  const { state } = useAppContext();
  const isSuperAdmin = state.loggedInUserInfo?.role === UserRoles.superadmin;
  const [question, setQuestion] = React.useState("");
  const [asking, setAsking] = React.useState(false);
  const [threads, setThreads] = React.useState<ChatThread[]>([]);
  const [expandedId, setExpandedId] = React.useState<string | false>(false);
  const [historyLoaded, setHistoryLoaded] = React.useState(false);
  const [openContactSupport, setOpenContactSupport] = React.useState(false);
  const listRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!listRef.current || !expandedId) return;
    const openItem = listRef.current.querySelector(
      `[data-thread-id="${expandedId}"]`
    );
    openItem?.scrollIntoView({ block: "nearest" });
  }, [expandedId, asking]);

  React.useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 120);
    return () => window.clearTimeout(timer);
  }, [open]);

  React.useEffect(() => {
    if (!open || !historyLoaded) return;
    const timer = window.setTimeout(() => {
      if (!listRef.current) return;
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }, 0);
    return () => window.clearTimeout(timer);
  }, [open, historyLoaded, asking, threads]);

  const askingRef = React.useRef(false);
  const sessionTurnsRef = React.useRef<{ question: string; answer: string }[]>([]);
  const sessionRootIdRef = React.useRef<string | null>(null);

  const loadHistory = React.useCallback(async () => {
    if (askingRef.current) return;
    try {
      const logs = await knowledgeArticleService.getMyLogs();
      setThreads(logsToThreads(logs || []));
      setHistoryLoaded(true);
      setHistoryLoaded(true);
    } catch (error) {
      console.error(error);
    }
  }, []);

  React.useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  React.useEffect(() => {
    knowledgeArticleService.getUnreadAdminNoteCount().then(setUnreadCount);
  }, [setUnreadCount]);

  React.useEffect(() => {
    const token = encryptStorageUtils.getAuthTokenFromStorage();
    const baseUrl = window.env.BACKEND_API_URL;
    if (!token || !baseUrl) return;

    const source = new EventSource(
      `${baseUrl.replace(/\/$/, "")}/datalake/knowledge/events?token=${encodeURIComponent(token)}`
    );
    source.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload?.type !== "admin-note") return;
        setUnreadCount((count) => count + 1);
        showSnackbar(
          "info",
          t("Ask Max"),
          t("A Super Admin added an answer to your question.")
        );
        loadHistory();
      } catch (error) {
        console.error(error);
      }
    };
    return () => source.close();
  }, [loadHistory, setUnreadCount, t]);

  const markThreadSeen = (thread: ChatThread) => {
    const ids = [thread, ...thread.followups]
      .filter((turn) => turn.unreadAdminUpdate)
      .map((turn) => turn.id);
    if (!ids.length) return;
    setThreads((prev) =>
      prev.map((item) =>
        item.id === thread.id
          ? {
              ...item,
              unreadAdminUpdate: false,
              followups: item.followups.map((followup) => ({
                ...followup,
                unreadAdminUpdate: false,
              })),
            }
          : item
      )
    );
    setUnreadCount((count) => Math.max(0, count - ids.length));
    knowledgeArticleService.markAdminNotesSeen(ids);
  };

  React.useEffect(() => {
    if (!open || asking) return;
    loadHistory();
  }, [asking, loadHistory, open]);

  React.useEffect(() => {
    if (!open) return;
    const rootId = sessionRootIdRef.current;
    const current =
      threads.find((thread) => rootId && thread.id === rootId) ||
      threads.find((thread) => thread.id.startsWith("live-"));
    if (current && threadHasUnread(current)) {
      markThreadSeen(current);
    }
  }, [open, threads]);

  const handleAsk = async (nextQuestion: string) => {
    const trimmed = nextQuestion.trim();
    if (!trimmed || asking) return;

    const parentId = sessionRootIdRef.current;
    const liveId = `live-${Date.now()}`;
    const fallbackAnswer = t(
      "I do not have knowledge for that yet. Try Onboarding Guides in the left menu, or send a message with Contact Us."
    );
    if (parentId) {
      setThreads((prev) =>
        prev.map((thread) =>
          thread.id === parentId
            ? {
                ...thread,
                followups: [
                  ...thread.followups,
                  { id: liveId, question: trimmed, replies: [] },
                ],
              }
            : thread
        )
      );
      setExpandedId(parentId);
    } else {
      setThreads((prev) => [
        ...prev,
        { id: liveId, question: trimmed, replies: [], followups: [] },
      ]);
      setExpandedId(liveId);
    }
    setQuestion("");
    askingRef.current = true;
    setAsking(true);

    const applyReply = (savedId: string, text: string, matches?: IKnowledgeMatch[]) => {
      const reply = { id: `${savedId}-a`, text, matches: matches || [] };
      if (parentId) {
        setThreads((prev) =>
          prev.map((thread) =>
            thread.id === parentId
              ? {
                  ...thread,
                  followups: thread.followups.map((followup) =>
                    followup.id === liveId
                      ? { ...followup, id: savedId, replies: [reply] }
                      : followup
                  ),
                }
              : thread
          )
        );
        setExpandedId(parentId);
        return;
      }
      if (savedId !== liveId) {
        sessionRootIdRef.current = savedId;
      }
      setExpandedId(savedId);
      setThreads((prev) =>
        prev.map((thread) =>
          thread.id === liveId
            ? { ...thread, id: savedId, replies: [reply] }
            : thread
        )
      );
    };

    try {
      const result = await knowledgeArticleService.ask(
        trimmed,
        location.pathname,
        sessionTurnsRef.current,
        parentId || undefined
      );
      const savedId = result?.logId || liveId;
      applyReply(savedId, result?.answer || fallbackAnswer, result?.matches || []);
      if (result?.answer) {
        sessionTurnsRef.current = [
          ...sessionTurnsRef.current,
          { question: trimmed, answer: result.answer },
        ].slice(-8);
      }
      const action = result?.action;
      if (
        action?.type === "openPage" &&
        (action.path === "/change-logs" || NavLinks.some((link) => link.path === action.path))
      ) {
        navigate(action.path);
      } else if (action && action.type !== "openPage") {
        const active = document.activeElement;
        if (active instanceof HTMLElement) active.blur();
        requestAction(action);
        if (location.pathname !== "/dashboard") {
          navigate("/dashboard");
        }
      }
    } catch (error) {
      console.error(error);
      applyReply(liveId, t("Failed to answer from Knowledge Pool"));
    } finally {
      askingRef.current = false;
      setAsking(false);
    }
  };

  const handleOpenPath = (path?: string) => {
    if (!path) return;
    setOpen(false);
    navigate(path);
  };

  const renderReplies = (replies: ChatReply[]) =>
    replies.map((reply) => (
      <Box key={reply.id}>
        {reply.fromAdmin ? (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mb: 0.5,
              fontWeight: 600,
              color: "warning.main",
            }}
          >
            {t("Updated by Super Admin")}
          </Typography>
        ) : null}
        <AssistanceMarkdown text={reply.text} />
        {reply.matches?.length ? (
          <Stack spacing={0.75} sx={{ mt: 0.75 }}>
            {reply.matches.map((match) => (
              <Box
                key={match._id}
                sx={{
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {match.title}
                </Typography>
                {match.relatedPath ? (
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    icon={<ArrowForwardIcon />}
                    label={`${t("Open page")}: ${pathLabel(match.relatedPath)}`}
                    onClick={() => handleOpenPath(match.relatedPath)}
                    sx={{ mt: 0.75 }}
                  />
                ) : null}
              </Box>
            ))}
          </Stack>
        ) : null}
      </Box>
    ));

  const sessionRootId = sessionRootIdRef.current;
  const currentThread =
    threads.find((thread) => sessionRootId && thread.id === sessionRootId) ||
    threads.find((thread) => thread.id.startsWith("live-"));
  const pastThreads = threads.filter((thread) => thread.id !== currentThread?.id);

  const renderCurrentChat = (thread: ChatThread) => {
    const turns = [thread, ...thread.followups];
    const pendingTurnId = turns[turns.length - 1]?.id;
    return (
    <Stack spacing={1.25}>
      {turns.map((turn) => (
        <Stack key={turn.id} spacing={1}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Box
              sx={{
                maxWidth: "88%",
                px: 1.25,
                py: 0.75,
                borderRadius: "16px 16px 4px 16px",
                bgcolor: "primary.main",
                color: "primary.contrastText",
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {turn.question}
              </Typography>
            </Box>
          </Box>
          {turn.replies.length ? (
            <Box
              sx={{
                maxWidth: "92%",
                px: 1.25,
                py: 0.75,
                borderRadius: "16px 16px 16px 4px",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
              }}
            >
              <Stack spacing={1}>{renderReplies(turn.replies)}</Stack>
            </Box>
          ) : null}
          {asking && turn.id === pendingTurnId && !turn.replies.length ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: 0.5 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                {t("Looking up knowledge...")}
              </Typography>
            </Box>
          ) : null}
        </Stack>
      ))}
    </Stack>
    );
  };

  const renderPastThread = (thread: ChatThread) => (
    <Accordion
      key={thread.id}
      data-thread-id={thread.id}
      disableGutters
      elevation={0}
      expanded={expandedId === thread.id}
      onChange={(_event, isExpanded) => {
        setExpandedId(isExpanded ? thread.id : false);
        if (isExpanded && threadHasUnread(thread)) {
          markThreadSeen(thread);
        }
      }}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "8px !important",
        bgcolor: (theme) =>
          theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
        "&:before": { display: "none" },
        overflow: "hidden",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 40,
          px: 1.25,
          "& .MuiAccordionSummary-content": {
            my: 0.75,
            minWidth: 0,
            width: "100%",
          },
        }}
      >
        <Box sx={{ minWidth: 0, width: "100%" }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              ...(expandedId === thread.id
                ? { whiteSpace: "pre-wrap", color: "primary.main" }
                : {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }),
            }}
          >
            {thread.question}
          </Typography>
          {thread.createdAt || thread.followups.length ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                pr:1
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {thread.createdAt
                  ? format(new Date(thread.createdAt), "MMM d, yyyy h:mm a")
                  : ""}
              </Typography>
              {thread.followups.length ? (
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {thread.followups.length > 1
                    ? `${thread.followups.length} ${t("Follow-up")}`
                    : t("Follow-up")}
                </Typography>
              ) : null}
            </Box>
          ) : null}
          {threadHasUnread(thread) ||
          thread.replies.some((reply) => reply.fromAdmin) ||
          thread.followups.some((followup) =>
            followup.replies.some((reply) => reply.fromAdmin)
          ) ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              {threadHasUnread(thread) ? (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "error.main",
                    flexShrink: 0,
                  }}
                />
              ) : null}
              <Typography
                variant="caption"
                sx={{ color: "warning.main", fontWeight: 600 }}
              >
                {t("Updated by Super Admin")}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.25, pt: 0, pb: 1.25 }}>
        <Stack spacing={1.25}>
          {renderReplies(thread.replies)}
          {thread.followups.map((followup) => (
            <Box
              key={followup.id}
              sx={{
                pt: 1.25,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  whiteSpace: "pre-wrap",
                  mb: 0.75,
                  color: "primary.main",
                }}
              >
                {followup.question}
              </Typography>
              <Stack spacing={1}>{renderReplies(followup.replies)}</Stack>
            </Box>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );

  const panel = (
    <Box
      sx={{
        width: isXs ? "100%" : ASSISTANCE_DRAWER_WIDTH_PX,
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: assistancePanelBg,
      }}
    >
            <Box
              sx={{
                px: 2,
                py: 0.8,
                pr: 0.6,
                display: "flex",
                alignItems: "center",
                bgcolor: theme.palette.mode === "dark" ?"#3c4554": "#676767",
                color: theme.palette.mode === "dark" ? "white" : "primary.contrastText",
              }}
            >
              {/* <HelpOutlineIcon sx={{ mr: 1 }} /> */}
              <img
                src={`/MaxBook.png`}
                style={{ width: "80px", height: "80px", marginLeft:"-16px", marginRight:"8px",
                  marginBottom: "-30px", marginTop: "-4px"
                 }}
              />
              <Box sx={{ flex: 1, minWidth: 0, mt: 0.2 }}>
                <Typography sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {t("Ask Max")}
                </Typography>
                <Typography variant="caption">
                  {t("Super AI agent")}
                </Typography>
              </Box>
              <Button
                color="inherit"
                startIcon={<SupportAgentIcon />}
                onClick={() => {
                  setOpen(false);
                  setOpenContactSupport(true);
                }}
                sx={{
                  mr: 1,
                  flexShrink: 0,
                  borderColor: "rgba(255,255,255,0.55)",
                  color: "inherit",
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  backgroundColor: "#0002",
                  px:2
                }}
              >
                {t("Contact Us")}
              </Button>
              <IconButton
                size="small"
                onClick={() => setOpen(false)}
                sx={{ color: "inherit" }}
                aria-label={t("Close")}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box
              ref={listRef}
              sx={{
                flex: 1,
                overflowY: "auto",
                px: 2,
                py: 1.5,
                bgcolor: assistancePanelBg,
              }}
            >
              <Stack spacing={1.5}>
                {pastThreads.length ? (
                  <Stack spacing={0.75}>
                    <Typography variant="body2" color="text.secondary">
                      {t("Previous questions")}
                    </Typography>
                    {pastThreads.map((thread) => renderPastThread(thread))}
                  </Stack>
                ) : null}
                {currentThread ? (
                  renderCurrentChat(currentThread)
                ) : (
                  <Box>
                    {pastThreads.length ? null : (
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          {t("Quick Actions")}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={0.75}
                          useFlexGap
                          sx={{ flexWrap: "wrap", mb: 2 }}
                        >
                          <Chip
                            size="small"
                            icon={<ExploreIcon />}
                            label={t("Onboarding Guides")}
                            onClick={() => handleOpenPath("/onboarding-guides")}
                          />
                          <Chip
                            size="small"
                            icon={<HistoryIcon />}
                            label={t("Change Logs")}
                            onClick={() => handleOpenPath("/change-logs")}
                          />
                          {isSuperAdmin ? (
                            <>
                              <Chip
                                size="small"
                                icon={<AutoStoriesIcon />}
                                label={t("Knowledge Pool")}
                                onClick={() => handleOpenPath("/knowledge-pool")}
                              />
                              <Chip
                                size="small"
                                icon={<HistoryIcon />}
                                label={t("Ask Logs")}
                                onClick={() => handleOpenPath("/ask-logs")}
                              />
                            </>
                          ) : null}
                        </Stack>
                      </>
                    )}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {t("Suggested questions")}
                    </Typography>
                    <Stack spacing={1} alignItems="flex-start">
                      {SUGGESTED_QUESTIONS.map((item) => (
                        <Chip
                          key={item}
                          label={t(item)}
                          onClick={() => handleAsk(item)}
                          sx={{ height: "auto", py: 0.75, whiteSpace: "normal" }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Box>

            <Box
              component="form"
              onSubmit={(event) => {
                event.preventDefault();
                handleAsk(question);
              }}
              sx={{
                p: 1.25,
                display: "flex",
                gap: 1,
                borderTop: "1px solid",
                borderColor: "divider",
                bgcolor: assistancePanelBg,
              }}
            >
              <TextField
                size="small"
                focused
                fullWidth
                inputRef={inputRef}
                autoFocus
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder={t("Ask Max...")}
                disabled={asking}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      color="primary"
                      type="submit"
                      disabled={asking || !question.trim()}
                      aria-label={t("Ask")}
                      sx={{ mr: "-8px"}}
                    >
                      <SendIcon />
                    </IconButton>
                  ),
                }}
              />
              
            </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        width,
        minWidth: 0,
        flexShrink: 0,
        alignSelf: "stretch",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        visibility: open ? "visible" : "hidden",
        borderLeft: open ? "1px solid" : "none",
        borderColor: "divider",
        bgcolor: assistancePanelBg,
        pointerEvents: open ? "auto" : "none",
        transition: theme.transitions.create("width", {
          easing: theme.transitions.easing.sharp,
          duration: open
            ? theme.transitions.duration.enteringScreen
            : theme.transitions.duration.leavingScreen,
        }),
      }}
    >
      {panel}
      {isXs && !open
        ? createPortal(
            <Box
              sx={{
                position: "fixed",
                right: 8,
                bottom: 8,
                zIndex: 1400,
              }}
            >
              <AskAnythingButton />
            </Box>,
            document.body
          )
        : null}
      <ContactSupportDialog
        open={openContactSupport}
        onClose={() => setOpenContactSupport(false)}
      />
    </Box>
  );
}
